'use client';

import { c as _c } from "react/compiler-runtime";
import { Picture, Tile } from '@petsmart-ui/sparky';
import { usePathname } from 'next/navigation';
import { useLayoutEffect, useState } from 'react';
import { getPromoOrigin, trackContentfulClick, trackingEvents, trackProductListFilter, trackTile6UpClick, trackVisualNavigationClicks } from '@libs/analytics';
import { BREAKPOINTS, clsx } from '@libs/common-next';
import { useBuildContext } from '../../../hooks';
import type { TilesProps } from '../types';
import { HorizontalCarousel } from './HorizontalCarousel';
import type { SixTileCarouselProps } from './types';
import './SixTileCarousel.scss';
export type FacetHitProps = {
  value: string;
  count: number;
};
export type DisjunctiveFacetsProps = {
  [key: string]: FacetHitProps[];
};
export default function SixTileCarousel(t0) {
  const $ = _c(59);
  let className;
  let header;
  let other;
  let overrideDefaultClass;
  let t1;
  let t2;
  let tiles;
  let variant;
  if ($[0] !== t0) {
    ({
      header,
      alignText: t1,
      tiles,
      className,
      variant,
      overrideDefaultClass,
      breakpoints: t2,
      ...other
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = header;
    $[3] = other;
    $[4] = overrideDefaultClass;
    $[5] = t1;
    $[6] = t2;
    $[7] = tiles;
    $[8] = variant;
  } else {
    className = $[1];
    header = $[2];
    other = $[3];
    overrideDefaultClass = $[4];
    t1 = $[5];
    t2 = $[6];
    tiles = $[7];
    variant = $[8];
  }
  const alignText = t1 === undefined ? "center" : t1;
  let t3;
  if ($[9] !== t2) {
    t3 = t2 === undefined ? {
      960: {
        perView: 3.5,
        gap: 16
      },
      560: {
        perView: 2.5,
        gap: 8
      }
    } : t2;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const breakpoints = t3;
  const pathname = usePathname();
  const buildContext = useBuildContext();
  let t4;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {};
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const [disjunctiveFacets, setDisjunctiveFacets] = useState(t4);
  const [isDragging, setIsDragging] = useState(false);
  let t5;
  if ($[12] !== disjunctiveFacets || $[13] !== variant) {
    t5 = name => {
      let results = 0;
      if (variant && disjunctiveFacets[variant]) {
        disjunctiveFacets[variant].map(facet => {
          if (facet?.value === name && facet?.count) {
            results = facet.count;
          }
          return results;
        });
      }
      return results;
    };
    $[12] = disjunctiveFacets;
    $[13] = variant;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const getSelectedPetResults = t5;
  const perViewDefault = tiles.length < 6 ? tiles.length : 6;
  let t6;
  if ($[15] !== className) {
    t6 = clsx("six-tile-carousel", className);
    $[15] = className;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const classNames = t6;
  let t7;
  if ($[17] !== buildContext || $[18] !== getSelectedPetResults || $[19] !== isDragging || $[20] !== pathname || $[21] !== variant) {
    t7 = (tile, index, clickedHeader) => {
      if (!tile?.link?.webUrl || isDragging) {
        return;
      }
      trackContentfulClick({
        position: index + 1,
        linkText: tile.title,
        linkUrl: tile.link.webUrl,
        promoTitle: clickedHeader ?? "",
        promoVariant: `Tile 6up - ${variant}`,
        promoOrigin: pathname ?? "",
        promoDesc: tile.internalTitle ?? "",
        promoType: `Tile 6up - ${variant}`
      });
      if (variant === "VisualNav") {
        if (getPromoOrigin(pathname) !== "homepage") {
          trackProductListFilter({
            position: index + 1,
            linkUrl: tile.link.webUrl,
            linkText: tile.title,
            promoTitle: clickedHeader ?? "",
            promoVariant: variant,
            filterSelection: tile.title,
            filterType: trackingEvents[variant].filter_type,
            results: getSelectedPetResults(tile.title)
          });
        }
        trackVisualNavigationClicks({
          categoryId: buildContext.pageId ?? "",
          navPos: index + 1,
          text: tile.title
        });
      }
    };
    $[17] = buildContext;
    $[18] = getSelectedPetResults;
    $[19] = isDragging;
    $[20] = pathname;
    $[21] = variant;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const tileClick = t7;
  let t8;
  if ($[23] !== variant) {
    t8 = () => {
      const handleFacets = data => {
        const facets = data as CustomEvent;
        const disjunctiveFacetsObj = {};
        if (variant && facets?.detail.variant === variant) {
          disjunctiveFacetsObj[variant] = facets?.detail.hits;
          setDisjunctiveFacets(prevDisjunctiveFacets => Object.assign(prevDisjunctiveFacets, disjunctiveFacetsObj));
        }
      };
      window.addEventListener("algoliaDisjunctiveFacets", data_0 => {
        handleFacets(data_0);
      });
      return () => {
        window.removeEventListener("algoliaDisjunctiveFacets", handleFacets);
      };
    };
    $[23] = variant;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== disjunctiveFacets || $[26] !== variant) {
    t9 = [variant, disjunctiveFacets];
    $[25] = disjunctiveFacets;
    $[26] = variant;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  useLayoutEffect(t8, t9);
  let t10;
  if ($[28] !== header || $[29] !== variant) {
    t10 = navigationClick => {
      if (variant) {
        trackTile6UpClick({
          variant,
          name: header ?? "",
          position: null,
          navigationClick
        });
      }
    };
    $[28] = header;
    $[29] = variant;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  const onArrowClick = t10;
  const t11 = overrideDefaultClass && className ? className : classNames;
  let t12;
  let t13;
  if ($[31] !== onArrowClick) {
    t12 = () => onArrowClick("left");
    t13 = () => onArrowClick("right");
    $[31] = onArrowClick;
    $[32] = t12;
    $[33] = t13;
  } else {
    t12 = $[32];
    t13 = $[33];
  }
  let t14;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = isDrag => setIsDragging(isDrag);
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== alignText || $[36] !== header || $[37] !== isDragging || $[38] !== tileClick || $[39] !== tiles || $[40] !== variant) {
    let t16;
    if ($[42] !== alignText || $[43] !== header || $[44] !== isDragging || $[45] !== tileClick || $[46] !== tiles.length || $[47] !== variant) {
      t16 = (tile_0, index_0) => <div key={tile_0.sysId || `${tile_0.title}-${index_0}-${tile_0.image?.remoteDesktopImageUrl || ""}`} className={tiles.length < 6 ? "carousel-item xl-static" : "carousel-item"}><Tile data-testid="six-tile-carousel-tile" title={tile_0.title} alignText={alignText} href={tile_0.link && !isDragging ? tile_0.link.webUrl : undefined} className="six-tile-item" onClick={() => {
          tileClick(tile_0, index_0, header);
          if (variant) {
            trackTile6UpClick({
              variant,
              name: tile_0.title,
              position: index_0 + 1,
              navigationClick: null
            });
          }
        }}><Picture imgSrc={tile_0.image.remoteDesktopImageUrl} items={[{
            media: `(max-width: ${BREAKPOINTS.md})`,
            srcSet: tile_0.image.remoteMobileImageUrl ?? tile_0.image.remoteDesktopImageUrl
          }, {
            media: `(min-width: ${BREAKPOINTS.md})`,
            srcSet: tile_0.image.remoteDesktopImageUrl
          }]} imgAlt={tile_0.image.webAltText} /></Tile></div>;
      $[42] = alignText;
      $[43] = header;
      $[44] = isDragging;
      $[45] = tileClick;
      $[46] = tiles.length;
      $[47] = variant;
      $[48] = t16;
    } else {
      t16 = $[48];
    }
    t15 = tiles.map(t16);
    $[35] = alignText;
    $[36] = header;
    $[37] = isDragging;
    $[38] = tileClick;
    $[39] = tiles;
    $[40] = variant;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[49] !== breakpoints || $[50] !== perViewDefault || $[51] !== t11 || $[52] !== t12 || $[53] !== t13 || $[54] !== t15) {
    t16 = <HorizontalCarousel className={t11} perViewDefault={perViewDefault} onLeftArrowClick={t12} onRightArrowClick={t13} breakpoints={breakpoints} onDragging={t14}>{t15}</HorizontalCarousel>;
    $[49] = breakpoints;
    $[50] = perViewDefault;
    $[51] = t11;
    $[52] = t12;
    $[53] = t13;
    $[54] = t15;
    $[55] = t16;
  } else {
    t16 = $[55];
  }
  let t17;
  if ($[56] !== other || $[57] !== t16) {
    t17 = <div className="six-tile-carousel-container" data-testid="six-tile-carousel" {...other}>{t16}</div>;
    $[56] = other;
    $[57] = t16;
    $[58] = t17;
  } else {
    t17 = $[58];
  }
  return t17;
}